/*
 * Player settings, kept in localStorage.
 *
 * These are *display* preferences and nothing else. The line matters enough to
 * state: the sky is computed the same way whatever the brightness curve says,
 * distances are measured and scored in kilometres whatever the unit setting
 * says, and nothing here reaches the server or the leaderboard. A setting that
 * could change a score would make two players' totals mean different things,
 * which is the one thing a shared daily game cannot afford.
 *
 * Adding a setting is one entry in SETTINGS plus a control in SettingsMenu.jsx.
 * The schema is what validates stored values, so a key that has been renamed or
 * a value that has been retired falls back to the default instead of reaching
 * the renderer.
 */

import { BRIGHTNESS_PRESETS, DEFAULT_PRESET } from './brightness.mjs';

const STORAGE_KEY = 'starguessr:settings';

/*
 * Each setting carries its own options, so the menu is generated from this
 * rather than hand-written twice. `slider: true` says the options are a single
 * ordered axis and should be dragged along rather than picked from.
 */
export const SETTINGS = {
  brightness: {
    label: 'Star brightness',
    slider: true,
    /*
     * The one line of prose in the menu. It earns its place by pointing at
     * something the page cannot do for you: past a point the limit is the panel,
     * not the curve, and someone dragging this to its brightest on a dimmed
     * laptop is turning the wrong dial.
     */
    note: "Consider increasing your device's screen brightness as well.",
    default: DEFAULT_PRESET,
    options: BRIGHTNESS_PRESETS.map((preset) => ({ value: preset.key, label: preset.label }))
  },
  units: {
    label: 'Distance',
    default: 'km',
    options: [
      { value: 'km', label: 'Kilometres' },
      { value: 'mi', label: 'Miles' }
    ]
  },
  clock: {
    label: 'Time',
    default: '12',
    options: [
      { value: '12', label: '12-hour' },
      { value: '24', label: '24-hour' }
    ]
  },
  invertDrag: {
    label: 'Drag to look',
    default: 'natural',
    options: [
      { value: 'natural', label: 'Normal' },
      { value: 'inverted', label: 'Inverted' }
    ]
  },
  palette: {
    label: 'Map pins',
    default: 'default',
    options: [
      { value: 'default', label: 'Default' },
      { value: 'colourblind', label: 'Colour-blind safe' }
    ]
  }
};

for (const spec of Object.values(SETTINGS)) {
  spec.values = spec.options.map((option) => option.value);
}

export const DEFAULTS = Object.fromEntries(
  Object.entries(SETTINGS).map(([key, spec]) => [key, spec.default])
);

/*
 * Everything the renderer sees goes through here, so a hand-edited or
 * out-of-date localStorage entry cannot put an unknown preset key into the star
 * curve or an unknown unit into the formatter.
 */
export function sanitise(raw) {
  const out = { ...DEFAULTS };
  if (!raw || typeof raw !== 'object') return out;
  for (const [key, spec] of Object.entries(SETTINGS)) {
    if (spec.values.includes(raw[key])) out[key] = raw[key];
  }
  return out;
}

function read() {
  try {
    return sanitise(JSON.parse(window.localStorage.getItem(STORAGE_KEY)));
  } catch {
    // Private windows, blocked site data, or something else's key in our slot.
    return { ...DEFAULTS };
  }
}

/*
 * A tiny store rather than props or context. Settings are read in three places
 * that are nowhere near each other in the tree — the sky renderer, the round
 * reveal and the results summary — and threading a settings object through every
 * component between them would touch far more code than the feature is worth.
 *
 * The snapshot is cached and only replaced on a write, so it is a stable
 * reference and `useSyncExternalStore` will not loop.
 */
let snapshot = null;
const listeners = new Set();

export function getSettings() {
  if (!snapshot) snapshot = read();
  return snapshot;
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setSetting(key, value) {
  const spec = SETTINGS[key];
  if (!spec || !spec.values.includes(value)) return getSettings();
  snapshot = { ...getSettings(), [key]: value };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // Not being able to remember the choice is not a reason to refuse it.
  }
  listeners.forEach((listener) => listener());
  return snapshot;
}

export function resetSettings() {
  snapshot = { ...DEFAULTS };
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // As above: forgetting is allowed to fail.
  }
  listeners.forEach((listener) => listener());
  return snapshot;
}

/*
 * A second tab changing a setting should not leave this one rendering the old
 * sky. The storage event only fires in *other* tabs, so this cannot loop.
 */
if (typeof window !== 'undefined' && window.addEventListener) {
  window.addEventListener('storage', (event) => {
    if (event.key && event.key !== STORAGE_KEY) return;
    snapshot = read();
    listeners.forEach((listener) => listener());
  });
}

/* ------------------------------------------------------------------ units */

/*
 * The international mile, exactly 1.609344 km by definition since 1959.
 *
 * Conversion happens at the point of display and nowhere else. `haversineKm`
 * and `scoreForDistance` never see this: a mile-reading player and a
 * kilometre-reading player are scored off the identical number, and a test
 * asserts it.
 */
export const KM_PER_MILE = 1.609344;

export const UNIT_LABEL = { km: 'km', mi: 'mi' };
export const UNIT_NAME = { km: 'Kilometres', mi: 'Miles' };

export function toDisplayDistance(km, units) {
  return units === 'mi' ? km / KM_PER_MILE : km;
}
